import { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PropTypes } from "prop-types";

import { useAuth } from "../../contexts/AuthContext";
import { sidebarPermissionForRoute } from "../../services/roles";
import { isDemoEmail } from "../../services/demoMode";

const GROUPS = [
  { id: "general", icon: "ni ni-app", match: ["/dashboard", "/files", "/search", "/ai-chat", "/ai/analysis"] },
  { id: "admin", icon: "ni ni-badge", match: ["/admin-panel", "/foundation"] },
  { id: "accounting", icon: "ni ni-money-coins", match: ["/accounting"] },
  { id: "inventory", icon: "ni ni-box-2", match: ["/inventory"] },
  { id: "procurement", icon: "ni ni-cart", match: ["/procurement"] },
  { id: "manufacturing", icon: "ni ni-settings", match: ["/manufacturing"] },
  { id: "quality", icon: "ni ni-check-bold", match: ["/quality", "/packaging"] },
  { id: "export", icon: "ni ni-world-2", match: ["/export"] },
  { id: "sales", icon: "ni ni-shop", match: ["/sales"] },
  { id: "afterSales", icon: "ni ni-support-16", match: ["/after-sales"] },
  { id: "enterprise", icon: "ni ni-building", match: ["/enterprise"] },
  { id: "settings", icon: "ni ni-settings-gear-65", match: ["/settings"] }
];

const STORAGE_KEY = "fd_sidebar_open_groups";

export function groupForPath(path) {
  return groupFor(path);
}

function groupFor(path) {
  const g = GROUPS.find((grp) => grp.match.some((m) => path === m || path.startsWith(`${m}/`)));
  return g ? g.id : "general";
}

function loadOpenGroups() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { general: true };
  } catch {
    return { general: true };
  }
}

const Sidebar = ({ routes, logo, open, onClose }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasPermission, isDemoUser, user } = useAuth() || {};
  const demoSession = (typeof isDemoUser === "function" && isDemoUser()) || isDemoEmail(user?.email);
  const [openGroups, setOpenGroups] = useState(loadOpenGroups);
  const [query, setQuery] = useState("");

  const visibleRoutes = useMemo(
    () =>
      routes.filter((prop) => {
        if (!prop.sidebar) return false;
        if (demoSession && (prop.hideInDemo || prop.path === "/admin-panel")) return false;
        const required = prop.permission || sidebarPermissionForRoute(prop);
        if (!required) return true;
        if (typeof hasPermission !== "function") return !demoSession;
        return hasPermission(required);
      }),
    [routes, demoSession, hasPermission]
  );

  const grouped = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return GROUPS.map((grp) => ({
      ...grp,
      items: visibleRoutes.filter((r) => {
        if (groupFor(r.path) !== grp.id) return false;
        if (!q) return true;
        const label = (r.nameKey ? t(r.nameKey) : r.name || "").toLocaleLowerCase();
        return label.includes(q) || t(`navGroups.${grp.id}`).toLocaleLowerCase().includes(q);
      })
    })).filter((grp) => grp.items.length > 0);
  }, [visibleRoutes, query, t]);

  const activeGroup = useMemo(() => {
    const sub = location.pathname.replace(/^\/admin/, "");
    return groupFor(sub);
  }, [location.pathname]);

  useEffect(() => {
    setOpenGroups({ [activeGroup]: true });
  }, [activeGroup]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    } catch {
      /* storage unavailable */
    }
  }, [openGroups]);

  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const displayName = user?.displayName || user?.email?.split("@")[0] || "User";
  const searching = query.trim().length > 0;

  return (
    <>
      <div
        className={`fd-sidebar-backdrop ${open ? "show" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`fd-sidebar ${open ? "open" : ""}`} aria-label={t("coreText.shell.mainNavigation")}>
        <div className="fd-sidebar__brand">
          <Link to={logo?.innerLink || "/admin/dashboard"} onClick={onClose} className="fd-sidebar__logo">
            {logo?.imgSrc ? <img src={logo.imgSrc} alt={logo.imgAlt || "FutureDocument"} /> : "FutureDocument"}
          </Link>
          <button
            type="button"
            className="fd-icon-btn d-lg-none"
            onClick={onClose}
            aria-label={t("shell.closeMenu")}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="fd-sidebar__search">
          <i className="fas fa-search" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("shell.menuSearch")}
            aria-label={t("shell.menuSearch")}
          />
        </div>

        <nav className="fd-sidebar__nav">
          {grouped.length === 0 && <p className="fd-sidebar__empty">{t("shell.noMenuResults")}</p>}
          {grouped.map((grp) => {
            const isOpen = searching || !!openGroups[grp.id];
            const single = grp.id === "general" || grp.id === "settings";
            return (
              <div key={grp.id} className={`fd-nav-group ${grp.id === activeGroup ? "is-active" : ""}`}>
                {!single && (
                  <button
                    type="button"
                    className="fd-nav-group__toggle"
                    onClick={() => toggleGroup(grp.id)}
                    aria-expanded={isOpen}
                  >
                    <i className={`${grp.icon} fd-nav-group__icon`} />
                    <span>{t(`navGroups.${grp.id}`)}</span>
                    <span className="fd-nav-group__count">{grp.items.length}</span>
                    <i className={`fas fa-chevron-down fd-nav-group__chevron ${isOpen ? "rot" : ""}`} />
                  </button>
                )}
                {(single || isOpen) && (
                  <ul className={`fd-nav-list ${single ? "" : "nested"}`}>
                    {grp.items.map((prop) => (
                      <li key={prop.path}>
                        <NavLink
                          to={prop.layout + prop.path}
                          onClick={onClose}
                          className={({ isActive }) =>
                            `fd-nav-link ${isActive || location.pathname.startsWith(`${prop.layout}${prop.path}/`) ? "active" : ""}`
                          }
                        >
                          <i className={prop.icon} />
                          <span>{prop.nameKey ? t(prop.nameKey) : prop.name}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="fd-sidebar__footer">
          <Link to="/admin/files/upload" onClick={onClose} className="btn btn-primary btn-block fd-sidebar__cta">
            <i className="ni ni-cloud-upload-96 mr-2" />
            {t("shell.quickUpload")}
          </Link>
          <Link to="/admin/settings/profile" onClick={onClose} className="fd-sidebar__user">
            <span className="fd-avatar">
              {user?.photoURL ? <img src={user.photoURL} alt="" /> : displayName.charAt(0).toUpperCase()}
            </span>
            <span className="fd-sidebar__user-text">
              <strong>{displayName}</strong>
              <small>{user?.email}</small>
            </span>
            {demoSession && <span className="badge badge-warning ml-auto">{t("shell.demoBadge")}</span>}
          </Link>
        </div>
      </aside>
    </>
  );
};

Sidebar.defaultProps = {
  routes: [{}],
  open: false,
  onClose: () => {}
};

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired
  })
};

export default Sidebar;
